'use client';

import { atom, useAtom } from 'jotai';
import { useEffect, useCallback } from 'react';
import { useRouter } from "@/i18n/navigation";
import { useQueryActivity } from '@/features/activity/services';
import { formatInnerJumpUrl, isJumpAction, jumpByAction } from '@/utils/jump-url';
import { useQueryClient } from '@tanstack/react-query';
import { queryString } from '@/utils/tools';
import { getUtmActFromStorage } from '@/utils/browser';
import { isInApp, openUrlInBrowser } from '@/libs/comeAppSDK';
import type { ActivitySourceEnum } from '@/const/report';

export interface ActivityItem {
  id: number;
  name: string;
  enName: string;
  startTime?: number;
  endTime?: number;
  coverImage: string;
  tabImage: {
    selected: string;
    default: string;
  };
  adImage: string;
  activityCommonDetail: {
    content: string;
    image: string;
    title: string;
  };
  isCommon: number; // 0 不是，1是通用活动模板
  activityLink: string;
  pageLink?: string;
  pageCommonDetail: {
    content: string;
    image: string;
    title: string;
  };
}

// 全部活动列表原子
export const activityListAtom = atom<ActivityItem[]>([]);

const isLoadedAtom = atom(false); // 记录是否已加载过数据

export const useActivityList = (source?: ActivitySourceEnum, initQuery?: boolean) => {
  const router = useRouter();
  const [activityList, setActivityList] = useAtom(activityListAtom);
  const queryClient = useQueryClient();
  const [isLoaded, setIsLoaded] = useAtom(isLoadedAtom); // 记录是否已加载过数据

  const utmActFromUrl = queryString<{ utm_act?: string; }>()?.utm_act;
  const utmActValue = utmActFromUrl || getUtmActFromStorage() || '';
  // 使用 react-query 查询活动列表，配置 staleTime 来复用请求
  const { data } = useQueryActivity.useQueryHook({
    utmAct: utmActValue,
    queryConfig: {
      enabled: initQuery !== false, // 如果 initQuery 为 false 则不自动查询
      staleTime: 1000, // 1秒内复用请求，不会重新发起请求
      refetchOnMount: true, // 只在数据 stale 时挂载时重新获取
      refetchOnWindowFocus: false, // 窗口聚焦时不重新获取
    }
  });

  // 当数据更新时，同步到 atom
  useEffect(() => {
    if (data) {
      setIsLoaded(true);
      if (data?.list) {
        setActivityList(data.list);
      }
    }
  }, [data, setActivityList]);

  const jumpTo = (item: ActivityItem, withTab?: boolean) => {
    // isCommon=0：已有落地页 URL
    // isCommon=1：无落地页，使用通用活动详情页
    /// 配置了跳转url
    if (item.isCommon === 0 && item.activityLink && !item?.pageCommonDetail?.title) {
      // 处理跳转动作
      if (isJumpAction(item.activityLink)) {
        jumpByAction(item.activityLink, source);
      } else if (/^http/.test(item.activityLink)) { // 外部链接
        if (isInApp()) {
          openUrlInBrowser(item.activityLink);
        } else {
          location.href = item.activityLink;
        }
      } else { // 内部链接
        const targetUrl = formatInnerJumpUrl(item.activityLink + `?detailId=${item.id}` + (withTab ? '&tab=activity' : '') + (source ? `&trackSource=${source}` : '')); // 进入项目内部的活动页需要带上tab的标记，让活动页顶部显示tab栏
        // 使用 next-intl 的 router，它会自动处理 locale 前缀
        router.push(targetUrl);
      }
    } else { // 未配置的时候则直接跳转到详情模板页
      const targetUrl = formatInnerJumpUrl(`/promo?id=${item.id}${withTab ? '&tab=activity' : ''}` + (source ? `&trackSource=${source}` : ''));
      // 使用 next-intl 的 router，它会自动处理 locale 前缀
      router.push(targetUrl);
    }
  };

  // 提供 refreshActivityList 作为手动刷新的方法
  // 使用 ensureQueryData 而不是 refetch，它会检查 staleTime 并自动去重
  const refreshActivityList = useCallback(async (): Promise<void> => {
    // ensureQueryData 会检查缓存，如果数据 fresh 则不会发起请求
    await queryClient.ensureQueryData(useQueryActivity.getQueryOptions({ utmAct: utmActValue }));
  }, [queryClient, utmActValue]);

  return {
    activityList,
    setActivityList,
    jumpTo,
    refreshActivityList,
    isLoaded,
  };
};


